/* Preview images for the canvas. The canvas never draws originals: small
   thumbnails when zoomed out, the 2048 px preview once a copy is big on screen. */

import { useEffect, useState } from 'react';
import type { AssetInfo } from '@gsb/core';

type Entry = { img: HTMLImageElement; ok: boolean; waiters: Set<() => void> };
const cache = new Map<string, Entry>();

function load(url: string): Entry {
  let e = cache.get(url);
  if (e) return e;
  const img = new Image();
  img.decoding = 'async';
  e = { img, ok: false, waiters: new Set() };
  cache.set(url, e);
  const entry = e;
  img.onload = () => { entry.ok = true; entry.waiters.forEach(w => w()); entry.waiters.clear(); };
  img.onerror = () => { cache.delete(url); entry.waiters.forEach(w => w()); entry.waiters.clear(); };
  img.src = url;
  return e;
}

export function preload(url?: string) { if (url) load(url); }

/** The best loaded image for an asset; asks for the big one when `big` is true. */
export function useAssetImage(asset: AssetInfo | undefined, big: boolean): HTMLImageElement | null {
  const [, tick] = useState(0);
  const thumb = asset?.thumbUrl, prev = asset?.previewUrl;
  const want = big ? prev : thumb;
  useEffect(() => {
    if (!want) return;
    const e = load(want);
    if (e.ok) return;
    const w = () => tick(n => n + 1);
    e.waiters.add(w);
    return () => { e.waiters.delete(w); };
  }, [want]);
  // the preview is always better once we have it
  const p = prev ? cache.get(prev) : undefined;
  if (p?.ok) return p.img;
  const t = thumb ? cache.get(thumb) : undefined;
  if (t?.ok) return t.img;
  return null;
}

let checker: HTMLCanvasElement | null = null;
/** 2×2 checker tile for the film (transparent areas). */
export function checkerTile(a = '#EEF3F6', b = '#FFFFFF', size = 7): HTMLCanvasElement {
  if (checker && checker.dataset.k === a + b + size) return checker;
  const c = document.createElement('canvas');
  c.width = c.height = size * 2;
  const x = c.getContext('2d')!;
  x.fillStyle = b; x.fillRect(0, 0, size * 2, size * 2);
  x.fillStyle = a; x.fillRect(0, 0, size, size); x.fillRect(size, size, size, size);
  c.dataset.k = a + b + size;
  checker = c;
  return c;
}

/** The image for a URL if it has loaded (starts loading it if not); `onLoad` is called once when it arrives. */
export function peekImage(url: string | undefined, onLoad?: () => void): HTMLImageElement | null {
  if (!url) return null;
  const e = load(url);
  if (e.ok) return e.img;
  if (onLoad) e.waiters.add(onLoad);
  return null;
}
